import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Paper,
  InputAdornment,
  Pagination
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import type { RestricaoPagto } from '../hooks/usePoliticasGlobais';

interface CondicaoLookup {
  codigo: number;
  descricao: string;
}

interface Props {
  condicoes: CondicaoLookup[];
  restricoes: RestricaoPagto[];
  onChange: (restricoes: RestricaoPagto[]) => void;
}

// AIDEV-NOTE: Gestão de condições de pagamento com busca e paginação.
// Usa `codigo` (integer do Firebird) como chave — sync_condicoes_pagto não tem coluna `id`.
// AIDEV-PERF: Renderiza apenas PAGE_SIZE itens por vez para evitar lag com muitas condições.

const PAGE_SIZE = 15;

export function CondicoesPagtoForm({ condicoes, restricoes, onChange }: Props) {
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);

  const getRestricao = useCallback(
    (codigo: number): RestricaoPagto => {
      return (
        restricoes.find((r) => r.condicao_pagto_id === codigo) ?? {
          condicao_pagto_id: codigo,
          ativo: true,
          valor_minimo_pedido: null
        }
      );
    },
    [restricoes]
  );

  const update = useCallback(
    (codigo: number, field: 'ativo' | 'valor_minimo_pedido', value: boolean | number | null) => {
      const existing = restricoes.find((r) => r.condicao_pagto_id === codigo);
      if (existing) {
        onChange(
          restricoes.map((r) => (r.condicao_pagto_id === codigo ? { ...r, [field]: value } : r))
        );
      } else {
        onChange([
          ...restricoes,
          { condicao_pagto_id: codigo, ativo: true, valor_minimo_pedido: null, [field]: value }
        ]);
      }
    },
    [restricoes, onChange]
  );

  // Filtro por busca
  const filtered = useMemo(() => {
    if (!busca.trim()) return condicoes;
    const term = busca.toLowerCase();
    return condicoes.filter((c) => c.descricao.toLowerCase().includes(term));
  }, [condicoes, busca]);

  // Marcar/desmarcar todas (aplica sobre os itens filtrados pela busca)
  const handleToggleAll = useCallback(
    (checked: boolean) => {
      const codigos = new Set(filtered.map((c) => c.codigo));
      const updated = restricoes.filter((r) => !codigos.has(r.condicao_pagto_id));
      const newEntries = filtered.map((c) => {
        const existing = restricoes.find((r) => r.condicao_pagto_id === c.codigo);
        return {
          condicao_pagto_id: c.codigo,
          ativo: checked,
          valor_minimo_pedido: existing?.valor_minimo_pedido ?? null
        };
      });
      onChange([...updated, ...newEntries]);
    },
    [filtered, restricoes, onChange]
  );

  // Paginação
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Typography variant="subtitle2">Condições de Pagamento ({filtered.length})</Typography>
        <TextField
          size="small"
          placeholder="Buscar condição..."
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: 220 }}
        />
      </Box>

      {/* Marcar/desmarcar todas */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'action.hover' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={filtered.length > 0 && filtered.every((c) => getRestricao(c.codigo).ativo)}
              indeterminate={
                filtered.some((c) => getRestricao(c.codigo).ativo) &&
                !filtered.every((c) => getRestricao(c.codigo).ativo)
              }
              onChange={(e) => handleToggleAll(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" fontWeight={600}>
              {busca.trim() ? 'Selecionar todas filtradas' : 'Selecionar todas'}
            </Typography>
          }
        />
      </Paper>

      {paginatedItems.map((c) => {
        const r = getRestricao(c.codigo);
        return (
          <Paper
            key={c.codigo}
            variant="outlined"
            sx={{ p: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <FormControlLabel
              sx={{ flex: 1 }}
              control={
                <Checkbox
                  checked={r.ativo}
                  onChange={(e) => update(c.codigo, 'ativo', e.target.checked)}
                />
              }
              label={<Typography variant="body2">{c.descricao}</Typography>}
            />
            {r.ativo && (
              <TextField
                label="Valor Mínimo (R$)"
                type="number"
                size="small"
                sx={{ width: 150 }}
                value={r.valor_minimo_pedido ?? ''}
                onChange={(e) =>
                  update(
                    c.codigo,
                    'valor_minimo_pedido',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
                placeholder="Sem mínimo"
                inputProps={{ min: 0 }}
              />
            )}
          </Paper>
        );
      })}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => setPage(p)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
